import ApiWithLogging from '../utils/apiWithLogging';
import { ReplacementType } from './deviceReplacementService';

// ประเภทข้อมูลการซ่อมบำรุงอุปกรณ์ - ดึงจาก maintenance_types API
export interface MaintenanceTypeInfo {
  id: number;
  code: string;
  name: string;
}

// ข้อมูลวิธีการแก้ไข - ดึงจาก repair_methods API
export interface RepairMethodInfo {
  id: number;
  code: string;
  name: string;
}

// ประเภทข้อมูลการซ่อมบำรุงอุปกรณ์
// ข้อมูลอุปกรณ์ย่อที่ list API ส่งมา (รหัสเครื่อง + SN) สำหรับแสดง/ลิงก์ในรายการ
export interface MaintenanceDeviceInfo {
  id: number;
  code?: string | null;
  serial_number?: string | null;
}

// ข้อมูล SIM ย่อสำหรับแสดงในประวัติการเปลี่ยนของเคสซ่อม
export interface MaintenanceSimInfo {
  id: number;
  phone_number?: string | null;
}

// รายการเปลี่ยนอุปกรณ์/SIM ที่ผูกกับเคสซ่อมบำรุงนี้ (device_replacements.maintenance_id)
export interface MaintenanceReplacementInfo {
  id: number;
  replacement_date: string;
  replacement_type: ReplacementType;
  old_device_condition?: string | null;
  old_device?: MaintenanceDeviceInfo | null;
  new_device?: MaintenanceDeviceInfo | null;
  old_sim?: MaintenanceSimInfo | null;
  new_sim?: MaintenanceSimInfo | null;
  reason?: string | null;
  replaced_by_name?: string | null;
}

// บริบทธุรกิจของรายการซ่อม — บริษัท/โครงการ/รหัส Activate/สัญญาเช่า (active หรือสัญญาล่าสุด)
export interface MaintenanceBusinessContext {
  maintenance_id: number;
  device_id: number | null;
  source: 'maintenance_link' | 'device_rental_active' | 'device_rental_historical' | null;
  company: { id: number; code?: string | null; name: string } | null;
  site: {
    id: number;
    code?: string | null;
    name: string;
    customer_code?: string | null;   // รหัส Activate จาก K-Visit
    activation_code?: string | null; // รหัส Activate เปิดใช้งาน
    contact_person?: string | null;
    phone?: string | null;
    project_phone?: string | null;
  } | null;
  rental: {
    id: number;
    rental_number: string;
    status: string;
    is_active: boolean;              // true=สัญญาที่ใช้งานอยู่ / false=สัญญาในอดีต
    rental_date?: string | null;
    expected_return_date?: string | null;
    billing_end_date?: string | null;
  } | null;
  // true เฉพาะเมื่ออุปกรณ์ของเคสนี้ถือครองอยู่ในสัญญาเช่าที่ ACTIVE — ใช้เปิดปุ่ม "เปลี่ยนอุปกรณ์" จากหน้าเคสซ่อม
  can_replace?: boolean;
  // rental_item ของสัญญาที่ active — ส่งตรงเป็น prop rentalItem ให้ DeviceReplacementModal ได้เลย (มีค่าเมื่อ can_replace = true)
  rental_item?: {
    id: number;
    device?: {
      id: number;
      code: string;
      serial_number: string;
      device_type_id?: number;
      device_type?: { id: number; name: string };
    };
    sim?: {
      id: number;
      phone_number: string;
      operator: string;
      expiry_date?: string;
    } | null;
    anydesk_id?: string | null;
  } | null;
}

// ข้อมูลโครงการย่อสำหรับแสดงในตาราง list (ชื่อ + รหัส Activate)
export interface MaintenanceSiteInfo {
  id: number;
  code?: string | null;
  name: string;
  customer_code?: string | null;   // รหัส Activate จาก K-Visit
  activation_code?: string | null; // รหัสเปิดใช้งาน
}

export interface DeviceMaintenance {
  id: number;
  device_id: number | null;
  device?: MaintenanceDeviceInfo | null; // ข้อมูลอุปกรณ์ (code/SN) — null ถ้า K-Visit match ไม่เจอ
  site?: MaintenanceSiteInfo | null;      // โครงการที่ผูกตรง (maintenance.site_id) — null ถ้าไม่ผูก
  maintenance_type_id: number;
  maintenance_type?: MaintenanceTypeInfo; // ข้อมูลประเภทการซ่อมบำรุง
  repair_method_id?: number | null;
  repair_method?: RepairMethodInfo | null; // ข้อมูลวิธีการแก้ไข
  maintenance_date: string;
  /** ผู้ดำเนินการ (พนักงาน) · null = ยังไม่มีผู้รับเรื่อง (เคสจากแอพ) — ชื่อคนแจ้งอยู่ที่ onsite_contact_name */
  performed_by: string | null;
  /** บัญชีพนักงานผู้ดำเนินการ (ใช้กรอง/จับคู่ dropdown — ชื่อเปลี่ยนได้ id ไม่เปลี่ยน) */
  performed_by_user_id?: number | null;
  details: string;
  result: string;
  user_id: number | null;
  status?: string; // ENHANCEMENT: สถานะการซ่อมบำรุง
  onsite_contact?: OnSiteContact; // ENHANCEMENT: ข้อมูลติดต่อหน้างาน (แบบ object)
  // ENHANCEMENT: ข้อมูลติดต่อหน้างาน (แบบ flat fields - รองรับทั้ง 2 รูปแบบ)
  onsite_contact_name?: string;
  onsite_contact_phone?: string;
  onsite_contact_phone_alt?: string;
  onsite_contact_name_alt?: string; // ชื่อผู้ติดต่อของเบอร์สำรอง
  device_app_version?: string;
  device_cache_size?: string;
  device_uptime?: string;
  // AnyDesk ของเครื่องในเคสนี้ — ความจริงอยู่ที่ rental_items (ของรอบเช่า) · computed จาก backend
  // source: 'rental_item' (รายการเช่าที่ผูกกับเคส) | 'device_active_rental' (สัญญาที่เครื่องอยู่ตอนนี้)
  //       | null (เครื่องไม่ได้อยู่ในสัญญา — ไม่มี fallback ไปค่าเก่าของเครื่องอีกแล้ว · 23 ก.ย. 2026)
  // แก้ผ่าน PUT /device-maintenance/{id}/remote-access (remoteAccessService.updateMaintenance)
  remote_access?: {
    anydesk_id?: string | null;
    remote_access_notes?: string | null;
    source?: 'rental_item' | 'device_active_rental' | string | null;
    rental_item_id?: number | null;
    rental_number?: string | null;
    // false = แถวของสัญญาที่จบแล้ว (อ่านอย่างเดียว · PUT จะตอบ 400) — ไม่ส่งมา (backend เก่า) = แก้ได้
    editable?: boolean;
  } | null;
  // เคสนี้มีการเปลี่ยนอุปกรณ์/SIM ผูกอยู่หรือไม่ (device_replacements.maintenance_id)
  has_replacement?: boolean;
  replacements?: MaintenanceReplacementInfo[];
  // ที่มาจากแอพภายนอก (/external-report) — null/undefined = เคสที่คีย์เองในระบบ
  // field เบามาทั้ง list + detail · external_payload (JSON ดิบ) มาเฉพาะ GET /{id}
  external_source?: string | null;          // 'K-VISIT' | 'K-SCAN-PLUS'
  external_request_id?: string | null;      // EXT-<ts>-<sn|no-sn> ใช้ค้น log ฝั่ง API
  external_serial_number?: string | null;   // SN ดิบที่แอพส่ง (ก่อน normalize) — null = แอพไม่ได้ส่ง
  external_customer_code?: string | null;   // รหัส Activate ดิบ
  external_topic?: string | null;
  // serial_exact | serial_normalized | site_single_device (ผูกจากโครงการเพราะแอพไม่ส่ง SN — ต้องเตือนให้ตรวจ)
  external_match_method?: string | null;
  external_payload?: Record<string, unknown> | null;
  created_at: string;
  updated_at: string | null;
}

// ข้อมูลสำหรับการสร้างการซ่อมบำรุงใหม่
export interface DeviceMaintenanceForm {
  // สถานะเริ่มต้น (user feedback 2026-08-31): บันทึกงานที่เสร็จแล้ว → ส่ง CLOSED ได้เลย · ไม่ส่ง = OPEN
  status?: string;
  device_id: number;
  maintenance_type_id: number;
  repair_method_id?: number;
  maintenance_date?: string; // ถ้าไม่ระบุจะใช้เวลาปัจจุบัน
  performed_by: string;
  performed_by_user_id?: number;
  details?: string;
  result?: string;
  user_id?: number;
  // Business Context (Phase 1 Enhancement)
  rental_id?: number;
  company_id?: number;
  site_id?: number;
  // ENHANCEMENT: On-Site Contact
  onsite_contact?: OnSiteContact;
}

// ข้อมูลสำหรับการอัปเดตการซ่อมบำรุง
export interface DeviceMaintenanceUpdateForm {
  device_id?: number;
  maintenance_type_id?: number | null;
  repair_method_id?: number;
  maintenance_date?: string;
  performed_by?: string;
  performed_by_user_id?: number;
  details?: string;
  result?: string;
  user_id?: number;
  // Business context ของเครื่องที่เลือกในโหมดแก้ไข (ให้ตรงกับ create form — Tiwakorn 2026-08-26)
  rental_id?: number;
  company_id?: number;
  site_id?: number;
  // ENHANCEMENT: On-Site Contact (เพิ่มเข้ามาใน Update Form)
  onsite_contact?: OnSiteContact;
}

// ข้อมูลสำหรับการล้างแคช
export interface ClearCacheForm {
  performed_by: string;
  details?: string;
  result?: string;
  user_id?: number;
}

// พารามิเตอร์สำหรับการดึงข้อมูลการซ่อมบำรุง
export interface GetDeviceMaintenanceParams {
  page?: number;
  limit?: number;
  device_id?: number;
  maintenance_type_id?: number;
  from_date?: string;
  to_date?: string;
  search?: string;
  status?: string; // ENHANCEMENT: Filter by status
  // Business Context Filters (Phase 2 Enhancement)
  company_id?: number;
  site_id?: number;
  rental_id?: number;
  // true = เฉพาะเคสที่มีการเปลี่ยนอุปกรณ์/SIM ผูกอยู่ · false = เฉพาะเคสที่ไม่มี · ไม่ระบุ = ทั้งหมด
  has_replacement?: boolean;
  /** เฉพาะเคสที่ยังไม่ปิด (เปิด/รอดำเนินการ/กำลังติดตาม) */
  open_only?: boolean;
  /** ดำเนินการโดย — user id หลายคน (OR กัน) */
  performer_ids?: number[];
  /** รวมเคสที่ยังไม่มีผู้รับเรื่อง (OR กับ performer_ids) */
  performer_unassigned?: boolean;
}

// การตอบกลับจากการดึงข้อมูลการซ่อมบำรุง
export interface GetDeviceMaintenanceResponse {
  items: DeviceMaintenance[];
  total: number;
  page?: number;
  limit?: number;
}

// ข้อมูลสำหรับเปลี่ยนสถานะ (Status Change)
export interface StatusChangeForm {
  new_status: string;
  reason?: string;
  repair_method_id?: number; // วิธีการแก้ไข — ใช้ตอนปิดงาน (CLOSED)
  // user feedback 2026-08-31: บันทึกผู้ดำเนินการ (บังคับใน modal, default = ผู้ล็อกอิน)
  // + ประเภทการซ่อมบำรุง (ไม่บังคับ · null = ล้างค่า) ไปพร้อมการเปลี่ยนสถานะ
  performed_by?: string;
  /** บัญชีพนักงานผู้ดำเนินการ — backend ถือ id เป็นหลัก ชื่อเอาจากบัญชี */
  performed_by_user_id?: number;
  maintenance_type_id?: number | null;
  // Optimistic-lock guard (FLOW-MNT-014 fix): สถานะที่ผู้ใช้เห็นตอนเปิด modal
  // backend เทียบกับสถานะปัจจุบันจริง — ถ้าถูกเปลี่ยนโดยคนอื่นระหว่างนั้น → 409 กัน lost-update
  expected_current_status?: string;
}

// ผู้ดำเนินการเคสซ่อม (ทีม) — feedback 29 ก.ย. 2026
export interface MaintenancePerformer {
  id: number;
  full_name: string | null;
  username: string;
  display_name: string;
}

export interface MaintenancePerformersResponse {
  configured: boolean;
  items: MaintenancePerformer[];
}

export interface MaintenancePerformerPool {
  configured: boolean;
  selected_ids: number[];
  users: (MaintenancePerformer & { role_name: string | null; can_sign_in: boolean })[];
}

// ---------- แท็บ "ประสิทธิภาพทีม" (GET /device-maintenance/performance) ----------
export interface PerformanceKpis {
  created: number | null;
  duplicates_excluded: number | null;
  born_closed: number | null;
  wait_median_min: number | null;
  wait_p90_min: number | null;
  wait_n: number | null;
  resolve_median_min: number | null;
  resolve_p90_min: number | null;
  resolve_n: number | null;
  sla_accept_rate: number | null;
  sla_resolve_rate: number | null;
  repeat_rate: number | null;
  reopen_rate: number | null;
}

export interface PerformanceAgingBucket {
  key: 'lt1d' | 'd1_3' | 'd3_7' | 'gt7d' | 'unassigned' | string;
  label: string;
  count: number;
  case_ids: number[];
}

export interface PerformanceResponse {
  period: { from: string; to: string; prev_from: string; days: number; granularity: 'day' | 'week' };
  scope: { performer_id: number | null; team_user_ids: number[]; pool_configured: boolean };
  targets: { accept_h: number; resolve_h: number };
  kpis: PerformanceKpis;
  prev_kpis: PerformanceKpis;
  backlog: {
    total: number;
    unassigned: number;
    by_status: Record<string, number>;
    aging: PerformanceAgingBucket[];
    oldest_case_id: number | null;
  };
  flow: { start: string; created: number; closed: number }[];
  status_time: { week_start: string; n: number; open_h: number | null; pending_h: number | null; follow_up_h: number | null }[];
  people: {
    user_id: number;
    name: string;
    cases: number;
    share: number | null;
    holding: number | null;
    wait_median_min: number | null;
    resolve_median_min: number | null;
    resolve_p90_min: number | null;
    sla_resolve_rate: number | null;
    repeat_rate: number | null;
    reopen_rate: number | null;
    top_type: string | null;
    replacement_share: number | null;
    low_sample: boolean;
  }[];
  heatmap: { weekday: number; hour: number; count: number; wait_median_min: number | null }[];
  types: {
    maintenance_type_id: number;
    name: string;
    count: number;
    resolve_median_min: number | null;
    replacement_rate: number | null;
    repeat_rate: number | null;
    low_sample: boolean;
  }[];
  top_sites: { site_id: number; code: string | null; name: string; count: number; top_type: string | null }[];
  repeat_devices: { device_id: number; code: string | null; site_name: string | null; count: number; repeat_count: number }[];
  cases: PerformanceCase[];
}

export interface PerformanceCase {
  id: number;
  created_at: string;
  site_id: number | null;
  site_name: string | null;
  device_id: number | null;
  device_code: string | null;
  maintenance_type_id: number | null;
  type_name: string | null;
  status: string;
  performer_id: number | null;
  performer_name: string | null;
  channel: string | null;
  wait_min: number | null;
  resolve_min: number | null;
  is_duplicate: boolean;
  born_closed: boolean;
  over_accept_target: boolean;
  over_resolve_target: boolean;
  repeat: boolean;
  reopened: boolean;
  has_replacement: boolean;
  in_window: boolean;
  in_backlog: boolean;
}

export interface GetPerformanceParams {
  days: number;
  performer_id?: number;
  accept_target_h: number;
  resolve_target_h: number;
}

// ประวัติการเปลี่ยนสถานะ
export interface StatusHistory {
  id: number;
  maintenance_id: number;
  old_status?: string | null; // null = การสร้างรายการครั้งแรก
  new_status: string;
  change_reason?: string | null; // ตรงกับ API (เดิม type เขียนผิดเป็น reason → อ่านไม่เจอ)
  changed_by: string;
  user_id?: number | null;
  changed_at: string;
}

// ไฟล์แนบ
export interface MaintenanceAttachment {
  id: number;
  maintenance_id: number;
  file_name: string;
  file_path: string;
  file_size: number;
  file_type: string;
  description?: string;
  uploaded_by: string;
  uploaded_at: string;
}

// ประวัติการแก้ไข
export interface EditHistory {
  id: number;
  maintenance_id: number;
  field_name: string;
  old_value: string;
  new_value: string;
  edited_by: string;
  edited_at: string;
}

// Activity Timeline Item
export interface ActivityTimelineItem {
  id: number;
  // 'replacement' = การเปลี่ยนอุปกรณ์/SIM ที่ผูกกับเคส (user feedback 2026-08-31 เคส #254)
  // 'attachment_deleted' = ลบไฟล์แนบ (BUG-S2295-002 — details = snapshot ของไฟล์ที่ถูกลบ)
  type: 'status_change' | 'edit' | 'attachment' | 'attachment_deleted' | 'created' | 'replacement' | 'external_received' | 'auto_link';
  timestamp: string;
  user: string;
  description: string;
  details?: any;
}

// เครื่องทั้งหมดของโครงการของเคส — ให้ Support สลับเคสไปเครื่องที่เสียจริงได้
// (feedback ทิวากร เคส #537, 21 ก.ย. 2026 · GET /device-maintenance/{id}/site-devices)
/** ซิมที่ใช้คู่กับเครื่อง (FB-2026-0003) — backend services/device_sim_info.sim_brief
 *  date_kind ตัดสินจากประเภทซิม (ไม่ใช่ค่าย): 'activation' = รายเดือน → วันเริ่มใช้งาน · 'expiry' = รายปี/เติมเงิน → วันหมดอายุ */
export interface DeviceSimBrief {
  id: number;
  phone_number?: string | null;
  operator?: string | null;
  sim_type?: string | null;
  billing_cycle?: string | null;
  date_kind: 'activation' | 'expiry';
  date_value?: string | null;
  activation_date?: string | null;
  expiry_date?: string | null;
  /** ซิมถูกลบจากทะเบียนแล้ว — เบอร์ที่ส่งมาตัด _del_ ออกแล้ว */
  is_deleted?: boolean;
}

export interface CaseSiteDeviceItem {
  device_id: number;
  code?: string | null;
  serial_number?: string | null;
  imei?: string | null;                 // FB-2026-0003
  sim?: DeviceSimBrief | null;          // ซิมคู่กับเครื่อง (rental_items แถวเดียวกัน · เครื่องที่ถอดในเคสนี้ = ซิมตอนถอด)
  previous_sim?: DeviceSimBrief | null; // ซิมเดิมก่อนเปลี่ยนในเคสนี้
  device_name?: string | null;
  product_name?: string | null;
  rental_id?: number | null;
  rental_number?: string | null;
  rental_status?: string | null;
  anydesk_id?: string | null;
  open_case_count: number;
  is_current: boolean;          // เครื่องที่เคสผูกอยู่ตอนนี้
  is_reported_by_app: boolean;  // S/N ตรงกับที่แอปส่งมา (เครื่องที่กดแจ้ง)
  is_replacement_in_case?: boolean;  // เครื่องทดแทนที่เปลี่ยนให้ในเคสนี้ — สลับเคสไปไม่ได้ (KD3 S2382)
  in_site: boolean;             // false = เครื่องปัจจุบันของเคสแต่ไม่อยู่ในสัญญาที่ใช้งานของโครงการนี้
}

export interface CaseSiteDevicesResponse {
  maintenance_id: number;
  site_id: number | null;
  site_name: string | null;
  reported_serial_number: string | null;
  items: CaseSiteDeviceItem[];
}

// ข้อมูลติดต่อหน้างาน (On-Site Contact)
// null = "ล้างค่า" (ส่งขึ้น API แล้วลง DB เป็น NULL) · '' ใช้เฉพาะใน state ของฟอร์ม
export interface OnSiteContact {
  name?: string | null;
  phone?: string | null;
  // เบอร์สำรอง + ชื่อเจ้าของเบอร์สำรอง (คู่กัน — user feedback Support 2026-09-04:
  // "บางครั้งเบอร์สำรองจะเป็นชื่ออื่น ที่ไม่ใช่ผู้ติดต่อเบอร์หลัก")
  alternate_phone?: string | null;
  alternate_name?: string | null;
  app_version?: string | null;
  cache_size?: string | null;
  uptime?: string | null;
}

// บริการจัดการข้อมูลการซ่อมบำรุงอุปกรณ์
const deviceMaintenanceService = {
  // ดึงรายการการซ่อมบำรุงทั้งหมด
  async getMaintenanceRecords(params: GetDeviceMaintenanceParams = {}): Promise<GetDeviceMaintenanceResponse> {
    try {
      // ใช้ค่าเริ่มต้นถ้าไม่มีการระบุ
      // แปลง date string เป็น ISO datetime format สำหรับ API
      // from_date: 2025-12-17 -> 2025-12-17T00:00:00
      // to_date: 2025-12-17 -> 2025-12-17T23:59:59
      const fromDateISO = params.from_date
        ? `${params.from_date}T00:00:00`
        : undefined;
      const toDateISO = params.to_date
        ? `${params.to_date}T23:59:59`
        : undefined;

      const queryParams = {
        page: params.page || 1,
        limit: params.limit || 10,
        device_id: params.device_id,
        maintenance_type_id: params.maintenance_type_id,
        from_date: fromDateISO,
        to_date: toDateISO,
        search: params.search,
        status: params.status, // Status Filter (Bug Fix)
        // Business Context Filters (Phase 2 Enhancement)
        company_id: params.company_id,
        site_id: params.site_id,
        rental_id: params.rental_id,
        has_replacement: params.has_replacement,
        // เฉพาะเคสที่ยังไม่ปิด — ส่งเฉพาะตอนติ๊ก (ไม่ติ๊ก = ไม่ส่ง key)
        open_only: params.open_only ? true : undefined,
        // ดำเนินการโดย — ส่งเป็น "12,15" (backend รับข้อความ ไม่ใช่ array)
        performer_ids: params.performer_ids?.length ? params.performer_ids.join(',') : undefined,
        performer_unassigned: params.performer_unassigned ? true : undefined
      };

      // ใช้ endpoint /device-maintenance/ หรือ /device-maintenance/search/ ถ้ามีการกรอง
      // สำคัญ: ต้องมี trailing slash เพื่อป้องกัน 307 redirect
      let endpoint = '/device-maintenance/';

      // ถ้ามีการกรองโดย device_id ให้ใช้ endpoint เฉพาะ
      if (params.device_id) {
        endpoint = `/device-maintenance/device/${params.device_id}`;
        // ลบ device_id ออกจาก queryParams เพราะมันอยู่ใน path แล้ว
        delete queryParams.device_id;
      }
      // ถ้ามีการกรองอื่นๆ ให้ใช้ endpoint /search/
      else if (
        params.from_date ||
        params.to_date ||
        params.search ||
        params.maintenance_type_id ||
        params.status || // Status Filter (Bug Fix)
        params.company_id ||
        params.site_id ||
        params.rental_id ||
        params.has_replacement !== undefined ||
        params.open_only ||
        params.performer_ids?.length ||
        params.performer_unassigned
      ) {
        endpoint = '/device-maintenance/search/';
      }

      // ApiWithLogging.get() already returns response.data, not AxiosResponse
      const data = await ApiWithLogging.get<GetDeviceMaintenanceResponse>(endpoint, {
        params: queryParams
      });

      return data;
    } catch (error) {
      console.error('Error in getMaintenanceRecords service:', error);
      throw error;
    }
  },

  // ดึงข้อมูลการซ่อมบำรุงตาม id
  async getMaintenanceRecord(id: number): Promise<DeviceMaintenance> {
    try {
      const data = await ApiWithLogging.get<DeviceMaintenance>(`/device-maintenance/${id}`);
      return data;
    } catch (error) {
      console.error('Error in getMaintenanceRecord service:', error);
      throw error;
    }
  },

  // บริบทธุรกิจของรายการซ่อม (บริษัท/โครงการ/รหัส Activate/สัญญาเช่าที่ active)
  async getMaintenanceContext(id: number): Promise<MaintenanceBusinessContext> {
    const data = await ApiWithLogging.get<MaintenanceBusinessContext>(`/device-maintenance/${id}/context`);
    return data;
  },

  // เครื่องทั้งหมดของโครงการของเคส — สำหรับสลับเคสไปเครื่องที่เสียจริง (เคส #537)
  async getCaseSiteDevices(id: number): Promise<CaseSiteDevicesResponse> {
    try {
      const data = await ApiWithLogging.get<CaseSiteDevicesResponse>(`/device-maintenance/${id}/site-devices`);
      return data;
    } catch (error) {
      console.error('Error in getCaseSiteDevices service:', error);
      throw error;
    }
  },

  // สร้างการซ่อมบำรุงใหม่
  async createMaintenanceRecord(data: DeviceMaintenanceForm): Promise<DeviceMaintenance> {
    try {
      const result = await ApiWithLogging.post<DeviceMaintenance>('/device-maintenance/', data);
      return result;
    } catch (error) {
      console.error('Error in createMaintenanceRecord service:', error);
      throw error;
    }
  },

  // แก้ไขข้อมูลการซ่อมบำรุง
  async updateMaintenanceRecord(id: number, data: DeviceMaintenanceUpdateForm): Promise<DeviceMaintenance> {
    try {
      const result = await ApiWithLogging.put<DeviceMaintenance>(`/device-maintenance/${id}`, data);
      return result;
    } catch (error) {
      console.error('Error in updateMaintenanceRecord service:', error);
      throw error;
    }
  },

  // ลบการซ่อมบำรุง
  async deleteMaintenanceRecord(id: number): Promise<any> {
    try {
      const result = await ApiWithLogging.delete(`/device-maintenance/${id}`);
      return result;
    } catch (error) {
      console.error('Error in deleteMaintenanceRecord service:', error);
      throw error;
    }
  },

  // สร้างประวัติการล้างแคช
  async createClearCacheRecord(deviceId: number, data: ClearCacheForm): Promise<DeviceMaintenance> {
    try {
      const result = await ApiWithLogging.post<DeviceMaintenance>(
        `/device-maintenance/device/${deviceId}/clear-cache`,
        data
      );
      return result;
    } catch (error) {
      console.error('Error in createClearCacheRecord service:', error);
      throw error;
    }
  },

  // E3 (MANUAL_AUDIT_SYSTEM_FINDINGS): ลบ getMaintenanceReport() ออกแล้ว — หน้ารายการ
  // ซ่อมบำรุงถอด fetch/state/renderMaintenanceReport() ทิ้งไปก่อนหน้านี้ เหลือ wrapper
  // ลอยที่ไม่มีใครเรียก (0 caller ทั้ง codebase) · backend ยังมี endpoint
  // GET /device-maintenance/reports/by-type-and-date อยู่ ถ้าวันหน้าจะทำจอรายงานจริง
  // ค่อยเขียน wrapper ใหม่ให้ตรงกับสิ่งที่จอนั้นต้องใช้

  // === ENHANCEMENT: Status Management ===

  // เปลี่ยนสถานะการซ่อมบำรุง
  async changeStatus(id: number, data: StatusChangeForm): Promise<DeviceMaintenance> {
    try {
      const result = await ApiWithLogging.post<DeviceMaintenance>(
        `/device-maintenance/${id}/status`,
        data
      );
      return result;
    } catch (error) {
      console.error('Error in changeStatus service:', error);
      throw error;
    }
  },

  // ผู้ดำเนินการ (ทีม) สำหรับ dropdown + ตัวกรอง "ดำเนินการโดย" — configured=false = ยังไม่ตั้งรายชื่อ (ได้ทุกบัญชี)
  async getPerformers(): Promise<MaintenancePerformersResponse> {
    return ApiWithLogging.get<MaintenancePerformersResponse>('/device-maintenance/performers');
  },

  // หน้าตั้งค่ารายชื่อทีม (รูปเฟือง) — ต้องมี maintenance.edit
  async getPerformerPool(): Promise<MaintenancePerformerPool> {
    return ApiWithLogging.get<MaintenancePerformerPool>('/device-maintenance/performers/pool');
  },

  async updatePerformerPool(userIds: number[]): Promise<{ selected_ids: number[] }> {
    return ApiWithLogging.put<{ selected_ids: number[] }>('/device-maintenance/performers/pool', { user_ids: userIds });
  },

  // แท็บประสิทธิภาพทีม — ไม่ส่ง performer_id = ทั้งทีม
  async getPerformance(params: GetPerformanceParams): Promise<PerformanceResponse> {
    const query: Record<string, number> = {
      days: params.days,
      accept_target_h: params.accept_target_h,
      resolve_target_h: params.resolve_target_h,
    };
    if (params.performer_id != null) query.performer_id = params.performer_id;
    return ApiWithLogging.get<PerformanceResponse>('/device-maintenance/performance', { params: query });
  },

  // ดึงประวัติการเปลี่ยนสถานะ
  async getStatusHistory(id: number): Promise<StatusHistory[]> {
    try {
      const data = await ApiWithLogging.get<StatusHistory[]>(
        `/device-maintenance/${id}/status-history`
      );
      return data;
    } catch (error) {
      console.error('Error in getStatusHistory service:', error);
      throw error;
    }
  },

  // === ENHANCEMENT: Attachment Management ===

  // อัพโหลดไฟล์แนบ
  async uploadAttachment(
    id: number,
    file: File,
    description?: string
  ): Promise<MaintenanceAttachment> {
    try {
      const formData = new FormData();
      formData.append('file', file);
      if (description) {
        formData.append('description', description);
      }

      // Use raw axios for FormData (no ApiWithLogging needed for file upload)
      const response = await ApiWithLogging.post<MaintenanceAttachment>(
        `/device-maintenance/${id}/attachments`,
        formData
      );
      return response;
    } catch (error) {
      console.error('Error in uploadAttachment service:', error);
      throw error;
    }
  },

  // ดึงรายการไฟล์แนบทั้งหมด
  async getAttachments(id: number): Promise<MaintenanceAttachment[]> {
    try {
      const data = await ApiWithLogging.get<MaintenanceAttachment[]>(
        `/device-maintenance/${id}/attachments`
      );
      return data;
    } catch (error) {
      console.error('Error in getAttachments service:', error);
      throw error;
    }
  },

  // ดึงไฟล์แนบเป็น Blob (ใช้สำหรับ preview รูปภาพ + ดาวน์โหลด — ผ่าน maintenance.view guard
  // แทน /uploads/{file_path} เดิมที่ gate ด้วย documents.view ผิดสิทธิ์)
  async getAttachmentBlob(maintenanceId: number, attachmentId: number): Promise<Blob> {
    try {
      const data = await ApiWithLogging.get<Blob>(
        `/device-maintenance/${maintenanceId}/attachments/${attachmentId}/file`,
        { responseType: 'blob' }
      );
      return data;
    } catch (error) {
      console.error('Error in getAttachmentBlob service:', error);
      throw error;
    }
  },

  // ลบไฟล์แนบ
  async deleteAttachment(maintenanceId: number, attachmentId: number): Promise<void> {
    try {
      await ApiWithLogging.delete(
        `/device-maintenance/${maintenanceId}/attachments/${attachmentId}`
      );
    } catch (error) {
      console.error('Error in deleteAttachment service:', error);
      throw error;
    }
  },

  // === ENHANCEMENT: History & Timeline ===

  // ดึงประวัติการแก้ไข
  async getEditHistory(id: number): Promise<EditHistory[]> {
    try {
      const data = await ApiWithLogging.get<EditHistory[]>(
        `/device-maintenance/${id}/edit-history`
      );
      return data;
    } catch (error) {
      console.error('Error in getEditHistory service:', error);
      throw error;
    }
  },

  // ดึง Activity Timeline (รวมทุกอย่าง)
  async getActivityTimeline(id: number): Promise<ActivityTimelineItem[]> {
    try {
      const data = await ApiWithLogging.get<ActivityTimelineItem[]>(
        `/device-maintenance/${id}/activity`
      );
      return data;
    } catch (error) {
      console.error('Error in getActivityTimeline service:', error);
      throw error;
    }
  }
};

export default deviceMaintenanceService;
